import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

/**
 * Zone de dépôt d'assets — fichiers du bureau ET assets internes (historique,
 * Bible, bibliothèque, moodboard).
 *
 * POURQUOI CENTRALISER : chaque zone de drop de la Toolbox avait réimplémenté sa
 * propre lecture du dataTransfer, et aucune ne couvrait les mêmes formats — les
 * refs image lisaient `x-bible-image`, les refs vidéo `x-toolbox-asset`, jamais
 * les deux. Résultat : glisser la même vignette marchait ici et pas là, sans
 * qu'aucune règle ne l'explique. Un seul lecteur, tous les formats, partout.
 *
 * `accept` : "image" | "video" | "audio", ou un tableau. Filtre les fichiers du
 * bureau. Les assets INTERNES ne sont pas filtrés — leur type n'est pas toujours
 * connu au moment du drop, et l'appelant sait ce qu'il accepte.
 */

const INTERNAL_FORMATS = ["application/x-toolbox-asset", "application/x-bible-image"];

/**
 * Extrait { url, type } d'un dataTransfer, quel que soit le format de la source.
 * Le type vient de l'EXTENSION d'abord, du type déclaré ensuite (cf typeFromUrl) — sinon
 * les garde-fous des appelants, tous écrits `asset.type && …`, ne s'appliquent pas quand
 * la source est muette (drag `text/plain` nu), et ils se trompent quand elle ment : deux
 * producteurs annoncent `asset.type || "image"` par défaut (HistoryPanel, ToolHistoryBar),
 * ce qui suffit à faire passer un .mp4 pour une image. L'extension, elle, ne ment pas.
 * Reste null quand ni l'une ni l'autre ne dit rien (URL `blob:`, chemin sans suffixe).
 */
export function readDroppedAsset(dataTransfer) {
  for (const format of INTERNAL_FORMATS) {
    let raw = "";
    try { raw = dataTransfer?.getData(format) || ""; } catch { /* format absent */ }
    if (!raw) continue;
    if (raw.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.url) return { url: parsed.url, type: typeFromUrl(parsed.url) || parsed.type || null };
      } catch { /* JSON illisible → on tente le format suivant */ }
    } else {
      // x-bible-image ne transporte QUE des images (toutes les sources Bible).
      return { url: raw, type: typeFromUrl(raw) || (format === "application/x-bible-image" ? "image" : null) };
    }
  }
  let plain = "";
  try { plain = dataTransfer?.getData("text/plain") || ""; } catch { /* ignore */ }
  // Un texte quelconque déposé (ex: une phrase copiée) ne doit pas devenir une ref.
  if (plain && (plain.startsWith("http") || plain.startsWith("/api/") || plain.startsWith("blob:"))) {
    return { url: plain, type: typeFromUrl(plain) };
  }
  return null;
}

/** Compat : ne renvoie que l'URL. Préférer readDroppedAsset (qui porte le type). */
export function readDroppedUrl(dataTransfer) {
  return readDroppedAsset(dataTransfer)?.url ?? null;
}

/**
 * Props à étaler sur un média pour le rendre SAISISSABLE vers toute zone de dépôt.
 *
 * `url` doit être l'URL de l'ASSET, jamais celle passée à resolveMediaUrl() pour
 * l'affichage : cette dernière peut porter un `?w=` de vignette, et on déposerait
 * alors une miniature basse définition en guise de référence.
 */
export function assetDragProps(url, type = "image") {
  return {
    draggable: !!url,
    onDragStart: (e) => {
      if (!url) return;
      try {
        e.dataTransfer.setData("text/plain", url);
        e.dataTransfer.setData("application/x-toolbox-asset", JSON.stringify({ url, type }));
        e.dataTransfer.effectAllowed = "copy";
      } catch { /* dataTransfer indisponible → drag natif inoffensif */ }
    },
  };
}

// Extensions de repli quand le système ne fournit aucun type MIME. Cas réel : un .md
// sous Windows arrive avec `type: ""` (pas d'entrée registre) — l'input file voisin,
// lui, filtre par EXTENSION, donc le bouton marchait et le glisser-déposer non.
const EXTENSIONS_BY_TYPE = {
  text: /\.(md|txt|markdown)$/i,
  image: /\.(png|jpe?g|webp|gif|avif|bmp|heic|heif|hif)$/i,
  video: /\.(mp4|mov|webm|m4v|avi|mkv)$/i,
  audio: /\.(mp3|wav|ogg|aac|m4a|flac|opus)$/i,
};

/**
 * Type déduit de l'EXTENSION de l'URL, quand la source du drag n'en déclare aucun.
 *
 * POURQUOI : un drag `text/plain` nu ne porte jamais de type, et les dix zones de dépôt
 * testent toutes `asset.type && asset.type !== "image"` — un type absent DÉSARME donc le
 * garde-fou. Mesuré le 2026-09-09 : une vidéo de 8,8 Mo posée en référence image est
 * partie chez Claude dans un bloc `image/jpeg`, et l'API a rejeté la requête entière.
 * `null` quand l'extension ne dit rien (URL `blob:`, `/api/media/<uuid>` sans suffixe) :
 * on ne devine pas, et on ne bloque pas — le comportement d'avant est conservé.
 */
function typeFromUrl(url) {
  const path = String(url || "").split(/[?#]/)[0];
  return ["image", "video", "audio"].find((t) => EXTENSIONS_BY_TYPE[t].test(path)) || null;
}

export function matchesAccept(file, types) {
  const mime = file.type || "";
  if (mime && types.some((t) => mime.startsWith(`${t}/`))) return true;
  if (!mime) return types.some((t) => EXTENSIONS_BY_TYPE[t]?.test(file.name || ""));
  // MIME présent mais hors famille (ex: text/markdown pour accept "text") :
  // on retente par extension plutôt que de rejeter un fichier légitime.
  return types.some((t) => EXTENSIONS_BY_TYPE[t]?.test(file.name || ""));
}

export function useAssetDrop({ accept = "image", onFiles, onUrl, disabled = false }) {
  const { t } = useTranslation();
  const [isOver, setIsOver] = useState(false);
  const types = useMemo(() => (Array.isArray(accept) ? accept : [accept]), [accept]);

  const onDrop = useCallback(async (e) => {
    e.preventDefault();
    // Aucune zone de dépôt n'est imbriquée dans une autre aujourd'hui (la zone
    // « Média source » et les contrôles sont FRÈRES dans ToolSidePanel) : ce
    // stopPropagation est un filet pour le jour où ce ne sera plus vrai.
    e.stopPropagation();
    setIsOver(false);
    if (disabled) return;

    const dropped = Array.from(e.dataTransfer?.files || []);
    if (dropped.length) {
      const files = dropped.filter((f) => matchesAccept(f, types));
      if (files.length && onFiles) { await onFiles(files); return; }
      // Un fichier a bien été lâché mais aucun n'est du bon type. Sans ce message,
      // la zone s'allume, le drop est avalé et il ne se passe rien — soit très
      // exactement le symptôme « le drag and drop ne marche pas ».
      toast.error(t("assetDrop.onlyAccepts", { types: types.join(", ") }));
      return;
    }

    const asset = readDroppedAsset(e.dataTransfer);
    if (!asset?.url) {
      // Zone de TEXTE : ce qui est déposé est du contenu, pas une URL. Sans ce repli,
      // déclarer un onUrl sur le textarea de Batch confisquait le dépôt de texte
      // sélectionné — que le navigateur insérait nativement jusque-là.
      if (types.includes("text") && onUrl) {
        let raw = "";
        try { raw = e.dataTransfer?.getData("text/plain") || ""; } catch { /* ignore */ }
        if (raw.trim()) await onUrl(raw.trim());
      }
      return;
    }
    // Le type déclaré par la source est RESPECTÉ : sans ce garde-fou, la vidéo du
    // workspace (désormais saisissable) partait comme « image de référence » ou
    // comme piste audio, jusque dans un appel payant, sans un mot.
    // Une zone TEXTE accepte l'URL d'un média comme contenu (BatchControls colle des URLs
    // dans sa liste de prompts) : lui appliquer le garde-fou média confisquerait le dépôt.
    if (asset.type && !types.includes("text") && !types.includes(asset.type)) {
      toast.error(t("assetDrop.onlyAccepts", { types: types.join(", ") }));
      return;
    }
    if (onUrl) await onUrl(asset.url);
  }, [disabled, onFiles, onUrl, types, t]);

  const onDragOver = useCallback((e) => {
    // On ne confisque QUE ce qu'on sait traiter. Sans ce filtre, une zone posée sur
    // un textarea (Batch) empêcherait de déposer du texte sélectionné dedans : le
    // preventDefault ci-dessous annule le comportement natif du navigateur, et rien
    // ne le remplacerait puisqu'il n'y a ni fichier ni URL à traiter.
    const dtTypes = Array.from(e.dataTransfer?.types || []);
    const hasFiles = dtTypes.includes("Files");
    // On n'intercepte que ce qu'on sait traiter : des fichiers, ou une URL quand la
    // zone déclare un onUrl. Sinon on laisse le navigateur faire — c'est ce qui
    // permet de déposer du texte sélectionné dans le textarea de Batch.
    if (!hasFiles && !onUrl) return;

    // preventDefault est OBLIGATOIRE sur dragover : sans lui le navigateur refuse
    // le drop et l'événement `drop` n'est jamais émis.
    e.preventDefault();
    if (!disabled) setIsOver(true);
  }, [disabled, onUrl]);

  const onDragLeave = useCallback((e) => {
    // Passer sur un ENFANT émet un dragleave sur le conteneur : sans ce test, le
    // halo violet clignote pendant qu'on survole une zone qui contient des
    // vignettes ou un lecteur, et on croit que le dépôt est refusé.
    if (e?.currentTarget?.contains?.(e.relatedTarget)) return;
    setIsOver(false);
  }, []);

  // data-paste-drop inscrit la zone auprès de lib/pasteToDrop.js : ⌘V d'une image en la survolant = la déposer.
  return { isOver, dropProps: { onDrop, onDragOver, onDragLeave, "data-paste-drop": disabled ? "off" : types.join(",") } };
}

export default useAssetDrop;
